import { useState, useCallback, useRef } from 'react';
import { createEmptySheet, calcGrossWeight } from '@/utils/cad-sheet';

const MAX_HISTORY = 100;

export function useCADSheet() {
  const [data, setData] = useState(createEmptySheet);
  const historyRef = useRef([createEmptySheet()]);
  const pointerRef = useRef(0);
  const skipHistoryRef = useRef(false);

  const setDataWithHistory = useCallback((updater) => {
    setData(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (next === prev) return prev;
      if (!skipHistoryRef.current) {
        historyRef.current = historyRef.current.slice(0, pointerRef.current + 1);
        historyRef.current.push(next);
        if (historyRef.current.length > MAX_HISTORY) {
          historyRef.current = historyRef.current.slice(-MAX_HISTORY);
        }
        pointerRef.current = historyRef.current.length - 1;
      }
      return next;
    });
  }, []);

  const undo = useCallback(() => {
    if (pointerRef.current <= 0) return;
    pointerRef.current--;
    skipHistoryRef.current = true;
    setData(historyRef.current[pointerRef.current]);
    skipHistoryRef.current = false;
  }, []);

  const redo = useCallback(() => {
    if (pointerRef.current >= historyRef.current.length - 1) return;
    pointerRef.current++;
    skipHistoryRef.current = true;
    setData(historyRef.current[pointerRef.current]);
    skipHistoryRef.current = false;
  }, []);

  const canUndo = useCallback(() => pointerRef.current > 0, []);
  const canRedo = useCallback(() => pointerRef.current < historyRef.current.length - 1, []);

  const loadSheet = useCallback((sheetData) => {
    historyRef.current = [sheetData];
    pointerRef.current = 0;
    skipHistoryRef.current = true;
    setData(sheetData);
    skipHistoryRef.current = false;
  }, []);

  const updateField = useCallback((key, value) => {
    setDataWithHistory(prev => ({ ...prev, [key]: value }));
  }, [setDataWithHistory]);

  const recalcNetAndGross = useCallback((metals, stones, grossWeightManual, currentGross) => {
    const netWeight = metals.reduce((s, m) => s + (parseFloat(m.netWeight) || 0), 0);
    const net = netWeight > 0 ? netWeight.toFixed(2) : '';
    const gross = grossWeightManual ? currentGross : calcGrossWeight(metals, stones);
    return { netWeight: net, grossWeight: gross };
  }, []);

  // Stone CRUD
  const addStone = useCallback(() => {
    setDataWithHistory(prev => {
      const stones = [...prev.stones, { id: crypto.randomUUID(), category: 'Accent Stone', stone_category: 'Natural', stone_shape: 'Round', stone_quality: '', setting_type: 'Prong', size_mm: '', weight_per_stone: '', quantity: '', total_weight: 0, type: 'Diamond', ownership: '', cut: '' }];
      const { netWeight, grossWeight } = recalcNetAndGross(prev.metals, stones, prev.grossWeightManual, prev.grossWeight);
      return { ...prev, stones, netWeight, grossWeight };
    });
  }, [setDataWithHistory, recalcNetAndGross]);

  const importStones = useCallback((stones) => {
    setDataWithHistory(prev => {
      const allStones = [...prev.stones, ...stones.map(s => ({ ...s, id: crypto.randomUUID() }))];
      const { netWeight, grossWeight } = recalcNetAndGross(prev.metals, allStones, prev.grossWeightManual, prev.grossWeight);
      return { ...prev, stones: allStones, netWeight, grossWeight };
    });
  }, [setDataWithHistory, recalcNetAndGross]);

  const updateStone = useCallback((id, field, value) => {
    setDataWithHistory(prev => {
      const stones = prev.stones.map(s => s.id === id ? { ...s, [field]: value } : s);
      const { netWeight, grossWeight } = recalcNetAndGross(prev.metals, stones, prev.grossWeightManual, prev.grossWeight);
      return { ...prev, stones, netWeight, grossWeight };
    });
  }, [setDataWithHistory, recalcNetAndGross]);

  const removeStone = useCallback((id) => {
    setDataWithHistory(prev => {
      const stones = prev.stones.filter(s => s.id !== id);
      const { netWeight, grossWeight } = recalcNetAndGross(prev.metals, stones, prev.grossWeightManual, prev.grossWeight);
      return { ...prev, stones, netWeight, grossWeight };
    });
  }, [setDataWithHistory, recalcNetAndGross]);

  // Metal CRUD
  const addMetal = useCallback(() => {
    setDataWithHistory(prev => {
      if (prev.metals.length >= 3) return prev;
      return { ...prev, metals: [...prev.metals, { id: crypto.randomUUID(), karat: '14K', color: 'Yellow', netWeight: '' }] };
    });
  }, [setDataWithHistory]);

  const updateMetal = useCallback((id, field, value) => {
    setDataWithHistory(prev => {
      const metals = prev.metals.map(m => m.id === id ? { ...m, [field]: value } : m);
      const { netWeight, grossWeight } = recalcNetAndGross(metals, prev.stones, prev.grossWeightManual, prev.grossWeight);
      return { ...prev, metals, netWeight, grossWeight };
    });
  }, [setDataWithHistory, recalcNetAndGross]);

  const removeMetal = useCallback((id) => {
    setDataWithHistory(prev => {
      if (prev.metals.length <= 1) return prev;
      const metals = prev.metals.filter(m => m.id !== id);
      const { netWeight, grossWeight } = recalcNetAndGross(metals, prev.stones, prev.grossWeightManual, prev.grossWeight);
      return { ...prev, metals, netWeight, grossWeight };
    });
  }, [setDataWithHistory, recalcNetAndGross]);

  // Findings CRUD
  const addFinding = useCallback(() => {
    setDataWithHistory(prev => ({ ...prev, findings: [...prev.findings, { id: crypto.randomUUID(), type: '', customName: '', weight: '', details: '' }] }));
  }, [setDataWithHistory]);

  const updateFinding = useCallback((id, field, value) => {
    setDataWithHistory(prev => ({ ...prev, findings: prev.findings.map(f => f.id === id ? { ...f, [field]: value } : f) }));
  }, [setDataWithHistory]);

  const removeFinding = useCallback((id) => {
    setDataWithHistory(prev => ({ ...prev, findings: prev.findings.filter(f => f.id !== id) }));
  }, [setDataWithHistory]);

  const setGrossWeightManual = useCallback((manual) => {
    setDataWithHistory(prev => {
      if (!manual) return { ...prev, grossWeightManual: false, grossWeight: calcGrossWeight(prev.metals, prev.stones) };
      return { ...prev, grossWeightManual: true };
    });
  }, [setDataWithHistory]);

  // Image CRUD
  const setImage = useCallback((index, file) => {
    const url = URL.createObjectURL(file);
    setDataWithHistory(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === index ? { ...img, file, url } : img),
    }));
  }, [setDataWithHistory]);

  const removeImage = useCallback((index) => {
    setDataWithHistory(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === index ? { ...img, file: null, url: '', annotations: [], textBoxes: [], highlights: [] } : img),
    }));
  }, [setDataWithHistory]);

  const addImageSlot = useCallback(() => {
    setDataWithHistory(prev => ({
      ...prev,
      images: [...prev.images, { id: prev.images.length, file: null, url: '', label: `View ${prev.images.length + 1}`, annotations: [], textBoxes: [], highlights: [] }],
    }));
  }, [setDataWithHistory]);

  const removeImageSlot = useCallback((index) => {
    setDataWithHistory(prev => {
      if (prev.images.length <= 1) return prev;
      const img = prev.images[index];
      if (img.url) URL.revokeObjectURL(img.url);
      return { ...prev, images: prev.images.filter((_, i) => i !== index).map((img, i) => ({ ...img, id: i })) };
    });
  }, [setDataWithHistory]);

  const updateImageLabel = useCallback((index, label) => {
    setDataWithHistory(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === index ? { ...img, label } : img),
    }));
  }, [setDataWithHistory]);

  // Annotation CRUD
  const addAnnotation = useCallback((imageIndex, annotation) => {
    setDataWithHistory(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === imageIndex ? { ...img, annotations: [...img.annotations, annotation] } : img),
    }));
  }, [setDataWithHistory]);

  const updateAnnotation = useCallback((imageIndex, annotationId, updates) => {
    skipHistoryRef.current = true;
    setData(prev => ({
      ...prev,
      images: prev.images.map((img, i) =>
        i === imageIndex ? { ...img, annotations: img.annotations.map(a => a.id === annotationId ? { ...a, ...updates } : a) } : img
      ),
    }));
    skipHistoryRef.current = false;
  }, []);

  const removeAnnotation = useCallback((imageIndex, annotationId) => {
    setDataWithHistory(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === imageIndex ? { ...img, annotations: img.annotations.filter(a => a.id !== annotationId) } : img),
    }));
  }, [setDataWithHistory]);

  // TextBox CRUD
  const addTextBox = useCallback((imageIndex, textBox) => {
    setDataWithHistory(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === imageIndex ? { ...img, textBoxes: [...(img.textBoxes || []), textBox] } : img),
    }));
  }, [setDataWithHistory]);

  const updateTextBox = useCallback((imageIndex, textBoxId, updates) => {
    skipHistoryRef.current = true;
    setData(prev => ({
      ...prev,
      images: prev.images.map((img, i) =>
        i === imageIndex ? { ...img, textBoxes: (img.textBoxes || []).map(t => t.id === textBoxId ? { ...t, ...updates } : t) } : img
      ),
    }));
    skipHistoryRef.current = false;
  }, []);

  const removeTextBox = useCallback((imageIndex, textBoxId) => {
    setDataWithHistory(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === imageIndex ? { ...img, textBoxes: (img.textBoxes || []).filter(t => t.id !== textBoxId) } : img),
    }));
  }, [setDataWithHistory]);

  // Highlight CRUD
  const addHighlight = useCallback((imageIndex, highlight) => {
    setDataWithHistory(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === imageIndex ? { ...img, highlights: [...(img.highlights || []), highlight] } : img),
    }));
  }, [setDataWithHistory]);

  const updateHighlight = useCallback((imageIndex, highlightId, updates) => {
    setDataWithHistory(prev => ({
      ...prev,
      images: prev.images.map((img, i) =>
        i === imageIndex ? { ...img, highlights: (img.highlights || []).map(h => h.id === highlightId ? { ...h, ...updates } : h) } : img
      ),
    }));
  }, [setDataWithHistory]);

  const removeHighlight = useCallback((imageIndex, highlightId) => {
    setDataWithHistory(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === imageIndex ? { ...img, highlights: (img.highlights || []).filter(h => h.id !== highlightId) } : img),
    }));
  }, [setDataWithHistory]);

  // Reference images
  const setEngravingImage = useCallback((file) => {
    const url = URL.createObjectURL(file);
    setDataWithHistory(prev => ({
      ...prev,
      engravingImage: { id: 0, file, url, label: 'Engraving Reference', annotations: [], textBoxes: [], highlights: [] },
    }));
  }, [setDataWithHistory]);

  const removeEngravingImage = useCallback(() => {
    setDataWithHistory(prev => {
      if (prev.engravingImage?.url) URL.revokeObjectURL(prev.engravingImage.url);
      return { ...prev, engravingImage: null };
    });
  }, [setDataWithHistory]);

  const addCustomerImage = useCallback((file) => {
    const url = URL.createObjectURL(file);
    setDataWithHistory(prev => ({
      ...prev,
      customerImages: [...prev.customerImages, { id: prev.customerImages.length, file, url, label: 'Customer Reference', annotations: [], textBoxes: [], highlights: [] }],
    }));
  }, [setDataWithHistory]);

  const removeCustomerImage = useCallback((index) => {
    setDataWithHistory(prev => {
      const img = prev.customerImages[index];
      if (img?.url) URL.revokeObjectURL(img.url);
      return { ...prev, customerImages: prev.customerImages.filter((_, i) => i !== index).map((img, i) => ({ ...img, id: i })) };
    });
  }, [setDataWithHistory]);

  const reset = useCallback(() => {
    const empty = createEmptySheet();
    historyRef.current = [empty];
    pointerRef.current = 0;
    skipHistoryRef.current = true;
    setData(prev => {
      prev.images.forEach(img => { if (img.url) URL.revokeObjectURL(img.url); });
      if (prev.engravingImage?.url) URL.revokeObjectURL(prev.engravingImage.url);
      prev.customerImages.forEach(img => { if (img.url) URL.revokeObjectURL(img.url); });
      return empty;
    });
    skipHistoryRef.current = false;
  }, []);

  return {
    data,
    loadSheet,
    updateField,
    addStone, importStones, updateStone, removeStone,
    addMetal, updateMetal, removeMetal,
    addFinding, updateFinding, removeFinding,
    setGrossWeightManual,
    setImage, removeImage, addImageSlot, removeImageSlot, updateImageLabel,
    addAnnotation, updateAnnotation, removeAnnotation,
    addTextBox, updateTextBox, removeTextBox,
    addHighlight, updateHighlight, removeHighlight,
    setEngravingImage, removeEngravingImage,
    addCustomerImage, removeCustomerImage,
    reset, undo, redo, canUndo, canRedo,
  };
}
